import { CheckCircle2, Loader2, Send } from "lucide-react";
import { useEffect, useState } from "react";

import Button from "./Button.jsx";
import LinkButton from "./LinkButton.jsx";
import SectionHeader from "./SectionHeader.jsx";
import { issueCategories, outcomes } from "./categories.js";
import { submitComplaint } from "./api.js";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  category: "online-purchase",
  businessName: "",
  businessAddress: "",
  amountDisputed: "",
  desiredResolution: "",
  hasContactedBusiness: "yes",
  preferredContact: "email",
  description: "",
};

export default function Complaint({ navigate }) {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    function applyDraft(draft) {
      if (!draft) {
        return;
      }

      setForm((current) => ({
        ...current,
        category: draft.category || current.category,
        description: draft.description || current.description,
      }));
    }

    try {
      const savedDraft = JSON.parse(
        localStorage.getItem("ccsComplaintDraft") || "null"
      );

      applyDraft(savedDraft);
      localStorage.removeItem("ccsComplaintDraft");
    } catch (error) {
      localStorage.removeItem("ccsComplaintDraft");
    }

    function handleDraft(event) {
      applyDraft(event.detail);
    }

    window.addEventListener("ccs-complaint-draft", handleDraft);

    return () => {
      window.removeEventListener("ccs-complaint-draft", handleDraft);
    };
  }, []);

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setError("");

    try {
      const data = await submitComplaint(form);

      setResult(data);
      setStatus("success");
      setForm(emptyForm);
    } catch (err) {
      setError(err?.message || "The complaint could not be submitted.");
      setStatus("error");
    }
  };

  if (status === "success" && result) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="rounded-md border border-civic-line bg-white p-8 text-center shadow-sm">
          <CheckCircle2
            className="mx-auto h-12 w-12 text-signal-green"
            aria-hidden="true"
          />

          <h1 className="mt-4 text-2xl font-black text-civic-navy">
            Complaint prepared
          </h1>

          <p className="mt-3 text-slate-600">Your reference number is:</p>

          <div className="mx-auto mt-3 w-fit rounded-md bg-civic-sky px-4 py-2 text-xl font-black text-civic-blue">
            {result.caseNumber}
          </div>

          <p className="mx-auto mt-5 max-w-xl text-sm leading-6 text-slate-600">
            Save this number for your records. This portal helps organize your
            information for review or referral. It does not replace official
            agency filing requirements, deadlines, or legal advice.
          </p>

          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              onClick={() => {
                setStatus("idle");
                setResult(null);
                setError("");
              }}
            >
              Prepare another complaint
            </Button>

            <LinkButton href="/" navigate={navigate} variant="secondary">
              Return home
            </LinkButton>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="Complaint Preparation"
        title="Prepare consumer complaint information"
      >
        Use this form to organize the facts, business information, records, and
        requested outcome before filing with the appropriate office or agency.
      </SectionHeader>

      <form
        onSubmit={onSubmit}
        className="mt-8 grid gap-6 lg:grid-cols-[1fr_.42fr]"
      >
        <div className="space-y-6">
          <div className="rounded-md border border-civic-line bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-civic-navy">
              Consumer Information
            </h2>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="firstName">
                  First name
                </label>
                <input
                  id="firstName"
                  required
                  autoComplete="given-name"
                  className="field"
                  value={form.firstName}
                  onChange={(event) => update("firstName", event.target.value)}
                />
              </div>

              <div>
                <label className="label" htmlFor="lastName">
                  Last name
                </label>
                <input
                  id="lastName"
                  required
                  autoComplete="family-name"
                  className="field"
                  value={form.lastName}
                  onChange={(event) => update("lastName", event.target.value)}
                />
              </div>

              <div>
                <label className="label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  required
                  type="email"
                  autoComplete="email"
                  className="field"
                  value={form.email}
                  onChange={(event) => update("email", event.target.value)}
                />
              </div>

              <div>
                <label className="label" htmlFor="phone">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  className="field"
                  value={form.phone}
                  onChange={(event) => update("phone", event.target.value)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="label" htmlFor="address">
                  Mailing address
                </label>
                <input
                  id="address"
                  autoComplete="street-address"
                  className="field"
                  value={form.address}
                  onChange={(event) => update("address", event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-civic-line bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-civic-navy">
              Business and Dispute
            </h2>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="category">
                  Issue category
                </label>
                <select
                  id="category"
                  className="field"
                  value={form.category}
                  onChange={(event) => update("category", event.target.value)}
                >
                  {issueCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label" htmlFor="amountDisputed">
                  Amount disputed
                </label>
                <input
                  id="amountDisputed"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="field"
                  value={form.amountDisputed}
                  onChange={(event) =>
                    update("amountDisputed", event.target.value)
                  }
                />
              </div>

              <div>
                <label className="label" htmlFor="businessName">
                  Business name
                </label>
                <input
                  id="businessName"
                  required
                  className="field"
                  value={form.businessName}
                  onChange={(event) =>
                    update("businessName", event.target.value)
                  }
                />
              </div>

              <div>
                <label className="label" htmlFor="businessAddress">
                  Business address
                </label>
                <input
                  id="businessAddress"
                  className="field"
                  value={form.businessAddress}
                  onChange={(event) =>
                    update("businessAddress", event.target.value)
                  }
                />
              </div>

              <div>
                <label className="label" htmlFor="desiredResolution">
                  Desired resolution
                </label>
                <select
                  id="desiredResolution"
                  required
                  className="field"
                  value={form.desiredResolution}
                  onChange={(event) =>
                    update("desiredResolution", event.target.value)
                  }
                >
                  <option value="">Select resolution</option>
                  {outcomes.map((outcome) => (
                    <option key={outcome} value={outcome}>
                      {outcome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label" htmlFor="hasContactedBusiness">
                  Contacted business?
                </label>
                <select
                  id="hasContactedBusiness"
                  className="field"
                  value={form.hasContactedBusiness}
                  onChange={(event) =>
                    update("hasContactedBusiness", event.target.value)
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unsure">Not sure</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="label" htmlFor="description">
                  Describe the issue
                </label>
                <textarea
                  id="description"
                  required
                  minLength={25}
                  className="field min-h-40"
                  value={form.description}
                  onChange={(event) => update("description", event.target.value)}
                  placeholder="Include dates, what happened, who you contacted, what response you received, and what outcome you want."
                />
                <p className="help-text">
                  Do not include Social Security numbers, passwords, full
                  account numbers, or unnecessary sensitive information.
                </p>
              </div>
            </div>
          </div>
        </div>

        <aside className="h-fit rounded-md border border-civic-line bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-civic-navy">
            Before Submitting
          </h2>

          <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
            <li className="flex gap-2">
              <CheckCircle2
                className="mt-1 h-4 w-4 shrink-0 text-civic-blue"
                aria-hidden="true"
              />
              Keep receipts, contracts, photos, emails, letters, screenshots,
              and complaint numbers.
            </li>

            <li className="flex gap-2">
              <CheckCircle2
                className="mt-1 h-4 w-4 shrink-0 text-civic-blue"
                aria-hidden="true"
              />
              Contact the business in writing when appropriate and request a
              specific resolution.
            </li>

            <li className="flex gap-2">
              <CheckCircle2
                className="mt-1 h-4 w-4 shrink-0 text-civic-blue"
                aria-hidden="true"
              />
              This portal does not replace legal advice, court deadlines, or
              official filing requirements.
            </li>
          </ul>

          <div className="mt-5">
            <label className="label" htmlFor="preferredContact">
              Preferred contact
            </label>
            <select
              id="preferredContact"
              className="field"
              value={form.preferredContact}
              onChange={(event) =>
                update("preferredContact", event.target.value)
              }
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </div>

          {error && (
            <div
              className="mt-4 rounded-md bg-red-50 p-3 text-sm font-semibold text-signal-red"
              role="alert"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={status === "loading"}
            className="mt-5 w-full"
          >
            {status === "loading" ? (
              <Loader2
                className="h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}

            {status === "loading"
              ? "Preparing complaint..."
              : "Prepare complaint"}
          </Button>
        </aside>
      </form>
    </section>
  );
}
